"use client"

import Breadcrumb from "@/app/components/atomic/molecules/Breadcrumb"
import WPContentRenderer from "@/app/components/atomic/molecules/WPContentRenderer"
import {useTracking} from "@/app/contexts/tracking-context"
import {IWPContentPayload, TBreadcrumb} from "@/app/models/wp"
import {setupSidebarIntendedItemsOnScroll} from "@/app/utils/advertizing-block-utils"
import {faqSection, initResponsiveTables} from "@/app/utils/wp-utils"
import type {MarketRate} from "@/app/utils/shortcode-preprocessing"
import type {LemoineSeries} from "@/app/utils/lemoine-counter"
import Image from "next/image"
import {useEffect} from "react"
import SectionTitle from "@/app/components/atomic/molecules/SectionTitle";

interface WPPostContentProps {
    postContent: IWPContentPayload;
    children?: any;
    preprocessedTools?: Array<{
        id: string;
        showFiltersAbove?: boolean;
        toolData: any;
        template: any;
        filters: any[];
        expressionContext?: string;
        expressionOnLoad?: string;
    }>;
    preprocessedComparators?: Array<{
        id: string;
        showFiltersAbove?: boolean;
        maxResults?: number;
        filterListClasses?: string;
        gapBetweenFilters?: string;
        maxFiltersMobile?: number;
        templateConfig: any;
        filtersConfig: any;
        initialResults: any[];
    }>;
    marketRate?: MarketRate | null;
    lemoineSeries?: LemoineSeries | null;
    breadcrumb?: TBreadcrumb
}

export default function WPPostContent({postContent, children, preprocessedTools, preprocessedComparators, marketRate, lemoineSeries, breadcrumb}: WPPostContentProps) {
    const {track} = useTracking()

    useEffect(() => {
        faqSection()
        setupSidebarIntendedItemsOnScroll()
        // only init responsive tables on md and lower devices
        const isDeviceLowerThanMD = window.matchMedia("(max-width: 992px)").matches
        if (isDeviceLowerThanMD) {
            // TODO: try to render those tables server side
            initResponsiveTables()
        }
    }, [])

    // Fire tracking event for strategic page tracking
    useEffect(() => {
        if (typeof window !== "undefined") {
            // Extract first 2 segments from current URL path
            const pathSegments = window.location.pathname.split("/").filter(Boolean)
            const pageSlug = "/" + pathSegments.slice(0, 2).join("/")

            track({
                pageStrategique: postContent.strategicPage || false,
                pageCocon: pageSlug,
            })
        }
    }, [postContent.strategicPage, track])

    return (
        <div className="flex justify-between relative px-5 lg:px-0 mx-auto w-full lg:gap-16 z-10">
            <div className="px-2 md:px-4 mx-auto pt-5 w-full max-w-[960px]">
                {breadcrumb && <Breadcrumb breadcrumb={breadcrumb} />}

                <SectionTitle level="h1" theme="white" className="pb-10 pt-5">
                    {postContent.title}
                </SectionTitle>

                <div className="relative rounded-2xl overflow-hidden mb-6 h-[376px]">
                    {typeof postContent.featuredImage === "string" && (
                        <Image
                            priority
                            className="object-cover"
                            alt={postContent.title}
                            src={postContent.featuredImage}
                            fill
                        />
                    )}
                </div>

                {children}

                <WPContentRenderer
                    html={postContent.content}
                    preprocessedTools={preprocessedTools}
                    preprocessedComparators={preprocessedComparators}
                    marketRate={marketRate}
                    lemoineSeries={lemoineSeries}
                />
            </div>
        </div>
    )
}
